import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ArrowDownUp, ChevronDown } from "lucide-react";
import GiftCardIcon from "@/components/GiftCardIcon";
import { useNavigate } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const iconUrl = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);

export default function ExchangeCalculator() {
    const [cards, setCards] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [card, setCard] = useState(null);
    const [payout, setPayout] = useState(null);
    const [amount, setAmount] = useState(100);
    const [currency, setCurrency] = useState("USD");
    const [openSend, setOpenSend] = useState(false);
    const [openRecv, setOpenRecv] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const t = setTimeout(() => {
            api.get("/giftcards").then((r) => {
                setCards(r.data);
                if (r.data.length) setCard(r.data[0]);
            }).catch(() => {});
        }, 100);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        if (!cards.length) return;
        const t = setTimeout(() => {
            api.get("/payout-methods", { params: { currency } }).then((r) => {
                setPayouts(r.data || []);
                setPayout((p) => {
                    if (!r.data || r.data.length === 0) return null;
                    const stillThere = p && r.data.find((m) => m.code === p.code);
                    return stillThere || r.data[0];
                });
            }).catch(() => {});
        }, 150);
        return () => clearTimeout(t);
    }, [currency, cards.length]);

    // Digital rate
    const digitalRate = useMemo(() => {
        if (!card) return 0;
        return currency === "CAD" ? Number(card.rate_cad ?? card.rate) : Number(card.rate);
    }, [card, currency]);

    // Physical rate — only if different from digital
    const physicalRate = useMemo(() => {
        if (!card) return null;
        const pr = currency === "CAD"
            ? (card.rate_physical_cad ? Number(card.rate_physical_cad) : 0)
            : (card.rate_physical ? Number(card.rate_physical) : 0);
        if (!pr || pr === 0) return null;
        if (Math.abs(pr - digitalRate) < 0.001) return null;
        return pr;
    }, [card, currency, digitalRate]);

    const received = useMemo(() => {
        return Number(amount || 0) * digitalRate;
    }, [amount, digitalRate]);

    const symbol = currency === "CAD" ? "CA$" : "$";

    return (
        <div data-testid="exchange-calculator" className="bg-white rounded-3xl p-6 sm:p-7 shadow-floating border border-slate-100 max-w-md w-full">
            <div className="flex justify-end mb-3">
                <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="calc-currency-toggle">
                    {["USD", "CAD"].map((c) => (
                        <button key={c} onClick={() => setCurrency(c)} data-testid={`calc-currency-${c}`}
                            className={`px-3 py-1 text-xs font-bold rounded-full transition ${currency === c ? "bg-emerald-500 text-white" : "text-slate-600"}`}>
                            {c}
                        </button>
                    ))}
                </div>
            </div>

            {/* You Send */}
            <div className="rounded-2xl border border-slate-100 p-4 bg-slate-50/50">
                <div className="text-xs text-slate-500 mb-2">You Send</div>
                <div className="flex items-center justify-between gap-3">
                    <div className="relative flex-1">
                        <button type="button" onClick={() => { setOpenSend(!openSend); setOpenRecv(false); }}
                            data-testid="calc-card-trigger" className="flex items-center gap-2 text-left w-full">
                            {card && <GiftCardIcon code={card.code} iconUrl={card.icon_url} size={36} />}
                            <span className="font-semibold text-slate-900">{card ? `${card.name} Gift Card` : "Loading..."}</span>
                            <ChevronDown size={16} className="text-slate-400" />
                        </button>
                        {openSend && (
                            <div className="absolute z-30 top-full mt-2 left-0 bg-white border border-slate-200 rounded-xl shadow-floating w-60 max-h-72 overflow-auto scrollbar-thin">
                                {cards.map((c) => (
                                    <button key={c.code} onClick={() => { setCard(c); setOpenSend(false); }}
                                        className="flex items-center gap-3 w-full px-3 py-2 hover:bg-slate-50 text-sm"
                                        data-testid={`calc-card-option-${c.code.toLowerCase()}`}>
                                        <GiftCardIcon code={c.code} iconUrl={c.icon_url} size={28} />
                                        <span className="text-slate-700">{c.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="text-right">
                        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                            data-testid="calc-amount-input"
                            className="w-24 text-right text-2xl font-bold text-slate-900 bg-transparent outline-none" />
                        <div className="text-xs text-slate-500">{currency}</div>
                    </div>
                </div>

                {/* ── Rate Display ── show digital always, physical only if different */}
                {card && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                        {physicalRate ? (
                            // Two rates — digital vs physical
                            <div className="grid grid-cols-2 gap-2">
                                <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
                                    <div className="flex items-center gap-1 mb-0.5">
                                        <span className="text-xs">💻</span>
                                        <span className="text-xs font-semibold text-emerald-700">Digital Card</span>
                                    </div>
                                    <div className="text-sm font-bold text-emerald-600">
                                        {(digitalRate * 100).toFixed(0)}% rate
                                    </div>
                                    <div className="text-xs text-emerald-600/70">
                                        {symbol}{(Number(amount || 0) * digitalRate).toFixed(2)} payout
                                    </div>
                                </div>
                                <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                                    <div className="flex items-center gap-1 mb-0.5">
                                        <span className="text-xs">🪙</span>
                                        <span className="text-xs font-semibold text-amber-700">Physical Card</span>
                                    </div>
                                    <div className="text-sm font-bold text-amber-600">
                                        {(physicalRate * 100).toFixed(0)}% rate
                                    </div>
                                    <div className="text-xs text-amber-600/70">
                                        {symbol}{(Number(amount || 0) * physicalRate).toFixed(2)} payout
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // Single rate
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-500">Exchange rate</span>
                                <span className="text-xs font-bold text-emerald-600">
                                    {symbol}1 = {symbol}{digitalRate.toFixed(2)} ({(digitalRate * 100).toFixed(0)}%)
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="flex justify-center my-3">
                <button onClick={() => {}}
                    className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-card flex items-center justify-center hover:rotate-180 transition-transform duration-300"
                    data-testid="calc-swap-button" aria-label="Swap">
                    <ArrowDownUp size={16} className="text-slate-600" />
                </button>
            </div>

            {/* You Receive */}
            <div className="rounded-2xl border border-slate-100 p-4 bg-slate-50/50">
                <div className="text-xs text-slate-500 mb-2">You Receive</div>
                <div className="flex items-center justify-between gap-3">
                    <div className="relative flex-1">
                        <button type="button" onClick={() => { setOpenRecv(!openRecv); setOpenSend(false); }}
                            data-testid="calc-payout-trigger"
                            className="flex items-center gap-2 text-left w-full" disabled={!payout}>
                            {payout?.icon_url ? (
                                <img src={iconUrl(payout.icon_url)} alt="" className="w-9 h-9 rounded-full bg-white object-cover" loading="lazy" />
                            ) : (
                                <GiftCardIcon code={payout?.code || ""} size={36} />
                            )}
                            <span className="font-semibold text-slate-900">{payout?.name || "Select method"}</span>
                            <ChevronDown size={16} className="text-slate-400" />
                        </button>
                        {openRecv && (
                            <div className="absolute z-30 top-full mt-2 left-0 bg-white border border-slate-200 rounded-xl shadow-floating w-64 max-h-72 overflow-auto">
                                {payouts.length === 0 && <div className="px-4 py-3 text-xs text-slate-500">No methods for {currency}</div>}
                                {payouts.map((p) => (
                                    <button key={p.code} onClick={() => { setPayout(p); setOpenRecv(false); }}
                                        className="flex items-center gap-3 w-full px-3 py-2 hover:bg-slate-50 text-sm"
                                        data-testid={`calc-payout-option-${p.code.toLowerCase()}`}>
                                        {p.icon_url ? (
                                            <img src={iconUrl(p.icon_url)} alt="" className="w-7 h-7 rounded-full bg-white object-cover" loading="lazy" />
                                        ) : (
                                            <GiftCardIcon code={p.code} size={28} />
                                        )}
                                        <span className="text-slate-700">{p.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="text-right">
                        <div data-testid="calc-receive-amount" className="text-2xl font-bold text-slate-900">
                            {symbol}{received.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-slate-500">via {payout?.code || "—"}</div>
                    </div>
                </div>
            </div>

            <button onClick={() => navigate("/register")} data-testid="calc-exchange-now-button"
                className="mt-5 w-full bg-brand hover:bg-brand-500 text-white font-semibold py-3.5 rounded-2xl shadow-button transition-all hover:-translate-y-0.5">
                Exchange Now
            </button>

            <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                <div className="flex items-center gap-1.5 text-slate-600 justify-center"><span className="w-1.5 h-1.5 rounded-full bg-brand" />Best Rates</div>
                <div className="flex items-center gap-1.5 text-slate-600 justify-center"><span className="w-1.5 h-1.5 rounded-full bg-brand" />Instant Payment</div>
                <div className="flex items-center gap-1.5 text-slate-600 justify-center"><span className="w-1.5 h-1.5 rounded-full bg-brand" />100% Secure</div>
            </div>
        </div>
    );
}